/**
 * Wait Step Handler
 * Pauses workflow execution for a specified duration or until a specific time
 */

import type {
  StepExecutionContext,
  StepExecutionResult,
  WaitStepConfig
} from '../../types/workflow';

export async function executeWait(
  context: StepExecutionContext
): Promise<StepExecutionResult> {
  try {
    const config = context.step.config as WaitStepConfig;

    let waitUntil = new Date();

    // Wait for duration (in minutes)
    if (config.wait_duration && config.wait_duration > 0) {
      waitUntil.setMinutes(waitUntil.getMinutes() + config.wait_duration);
      console.log(`⏰ Waiting ${config.wait_duration} minutes until ${waitUntil.toISOString()}`);
    }
    // Wait until specific time of day
    else if (config.wait_until_time) {
      const [hours, minutes] = config.wait_until_time.split(':').map(Number);
      waitUntil.setHours(hours, minutes, 0, 0);

      // If time has passed today, wait until tomorrow
      if (waitUntil <= new Date()) {
        waitUntil.setDate(waitUntil.getDate() + 1);
      }

      console.log(`⏰ Waiting until time ${config.wait_until_time} (${waitUntil.toISOString()})`);
    }
    // Wait until specific day of week
    else if (config.wait_until_day !== undefined) {
      const currentDay = waitUntil.getDay();
      const targetDay = config.wait_until_day;

      let daysToAdd = targetDay - currentDay;
      if (daysToAdd <= 0) {
        daysToAdd += 7; // Next week
      }

      waitUntil.setDate(waitUntil.getDate() + daysToAdd);

      // If wait_until_time is also specified, set that time
      if (config.wait_until_time) {
        const [hours, minutes] = config.wait_until_time.split(':').map(Number);
        waitUntil.setHours(hours, minutes, 0, 0);
      }

      console.log(`⏰ Waiting until day ${targetDay} (${waitUntil.toISOString()})`);
    }
    // No wait configuration - move to next step immediately
    else {
      console.log('⏰ No wait configuration, proceeding immediately');
      return {
        success: true,
        nextStepId: context.step.next_step_id,
      };
    }

    // Return with shouldWait flag and waitUntil timestamp
    return {
      success: true,
      nextStepId: context.step.next_step_id,
      shouldWait: true,
      waitUntil,
    };
  } catch (error) {
    console.error('Error in wait handler:', error);
    return {
      success: false,
      error: (error as Error).message,
    };
  }
}
